import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const PRIORITIES = ['Critical', 'Major', 'Minor'];
const STATUSES   = ['Open', 'Ongoing', 'Blocked', 'On Hold', 'Closed'];

const PRIORITY_ACTIVE = {
  Critical: 'border-red-400 bg-red-50 text-red-700',
  Major:    'border-yellow-400 bg-yellow-50 text-yellow-700',
  Minor:    'border-green-400 bg-green-50 text-green-700',
};

export default function CreateSubtaskDialog({ taskId, onCreated, onClose }) {
  const apiUrl = process.env.REACT_APP_API_URL;
  const token  = localStorage.getItem('jwt') || localStorage.getItem('token');

  const [form, setForm] = useState({
    title: '', description: '', assigned_to: '', priority: 'Minor', status: 'Open', due_date: '',
  });
  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [empOpen, setEmpOpen]     = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');
  const dropRef = useRef();

  useEffect(() => {
    axios.get(`${apiUrl}/subtasks/employees/active`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setEmployees(r.data || [])).catch(() => {});
  }, [apiUrl, token]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setEmpOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredEmps = employees.filter(e => {
    const q = empSearch.toLowerCase();
    return (
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
      (e.employee_id || '').toLowerCase().includes(q)
    );
  });

  const selectEmployee = (emp) => {
    setSelectedEmp(emp);
    setForm(f => ({ ...f, assigned_to: emp.id }));
    setEmpSearch('');
    setEmpOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setError('Title is required.');
    if (!form.assigned_to) return setError('Please select an employee.');
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(
        `${apiUrl}/tasks/${taskId}/subtasks`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'Error creating subtask.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col border border-gray-100 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#f0f7e6] flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-[#7AA33E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 leading-tight">Create Subtask</h3>
              <p className="text-xs text-gray-400 mt-0.5">Add a subtask to this parent task</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition text-xl leading-none"
          >&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-6 flex flex-col gap-5 overflow-y-auto">
          {/* Title */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Title <span className="text-red-400">*</span></label>
            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Write unit tests for auth module"
              disabled={submitting}
              className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white disabled:opacity-50 transition"
              required
            />
          </div>

          <div className="border-t border-gray-100" />

          {/* Description */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</label>
              <span className="text-xs text-gray-400">Optional</span>
            </div>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe what needs to be done..."
              rows={3}
              disabled={submitting}
              className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white resize-none disabled:opacity-50 transition"
            />
          </div>

          <div className="border-t border-gray-100" />

          {/* Assign Employee */}
          <div className="flex flex-col gap-2" ref={dropRef}>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Assign Employee <span className="text-red-400">*</span></label>
            {selectedEmp ? (
              <div className="flex items-center justify-between rounded-xl border border-[#7AA33E] bg-[#f4f9ec] px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#7AA33E] flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                    {selectedEmp.first_name[0]}{selectedEmp.last_name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{selectedEmp.first_name} {selectedEmp.last_name}</p>
                    <p className="text-xs text-gray-500">{selectedEmp.employee_id} · Employee</p>
                  </div>
                </div>
                <button type="button" onClick={() => { setSelectedEmp(null); setForm(f => ({ ...f, assigned_to: '' })); }}
                  className="w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition text-sm">&times;</button>
              </div>
            ) : (
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                </svg>
                <input
                  value={empSearch}
                  onChange={e => { setEmpSearch(e.target.value); setEmpOpen(true); }}
                  onFocus={() => setEmpOpen(true)}
                  placeholder="Search by name or employee ID..."
                  disabled={submitting}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 text-gray-900 pl-9 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white disabled:opacity-50 transition"
                />
                {empOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
                    {filteredEmps.length === 0 ? (
                      <div className="flex flex-col items-center py-6 text-gray-400">
                        <svg className="w-7 h-7 mb-1.5 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-4-4H4a4 4 0 00-4 4v1h5m6-10a4 4 0 100-8 4 4 0 000 8z" /></svg>
                        <p className="text-xs">No employees found.</p>
                      </div>
                    ) : filteredEmps.map(emp => (
                      <button
                        key={emp.id}
                        type="button"
                        onClick={() => selectEmployee(emp)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 transition"
                      >
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xs flex-shrink-0">
                          {emp.first_name[0]}{emp.last_name[0]}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{emp.first_name} {emp.last_name}</p>
                          <p className="text-xs text-gray-400">{emp.employee_id} · Employee</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-gray-100" />

          {/* Priority */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Priority</label>
            <div className="grid grid-cols-3 gap-2">
              {PRIORITIES.map(p => (
                <button
                  key={p} type="button"
                  disabled={submitting}
                  onClick={() => setForm(f => ({ ...f, priority: p }))}
                  className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition disabled:opacity-50 ${
                    form.priority === p ? PRIORITY_ACTIVE[p] : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100" />

          {/* Status + Due Date side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</label>
              <select
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                disabled={submitting}
                className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white disabled:opacity-50 transition appearance-none cursor-pointer"
              >
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</label>
                <span className="text-xs text-gray-400">Optional</span>
              </div>
              <input
                type="date"
                value={form.due_date}
                onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                disabled={submitting}
                className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white disabled:opacity-50 transition"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-1 border-t border-gray-100">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-3 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:bg-[#5d8230] disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm shadow-[#7AA33E]/20 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Creating…</>
              ) : 'Create Subtask'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 disabled:opacity-50 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
